import { z } from 'zod';

import { formatPhoneUS } from '@/lib/utils/format';
import { emailAddressSchema } from '@/lib/validation/email';

/** `GET /profile/admin` (context.md §Backend contract). `phone` is the raw 10 digits. */
export const profileSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(), // grilling Q3: null for a Dealer with no email; every Role parses it
  phone: z.string(),
  role: z.string(),
  roleLabel: z.string(),
});
export type Profile = z.infer<typeof profileSchema>;

const NAME_MESSAGE = 'Enter a valid name';
const EMAIL_MESSAGE = 'Enter a valid email address';

/** The backend's own rule (`^[a-zA-Z][a-zA-Z ]*$`, 1–50, after trim) — so "starts with a letter"
 *  (§5c) is the regex, not a separate check. No whitespace collapsing: the server doesn't. */
const nameSchema = z
  .string()
  .trim()
  .pipe(
    z
      .string()
      .min(1, NAME_MESSAGE)
      .max(50, NAME_MESSAGE)
      .regex(/^[a-zA-Z][a-zA-Z ]*$/, NAME_MESSAGE),
  );

const emailBase = z.string().trim().toLowerCase().max(100, EMAIL_MESSAGE);

/** Phone and Role ride in the form only to be displayed read-only (§3d/§7a); they are always
 *  valid and never reach the wire — `updateProfile` sends exactly `{ firstName, lastName, email }`. */
export const profileFormSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailBase.pipe(emailAddressSchema(EMAIL_MESSAGE)),
  phone: z.string(),
  role: z.string(),
});

/** Grilling Q3: a Dealer's email is optional — blank goes on the wire as `null`; a non-blank value
 *  keeps the shipped format and 100-char rules. */
export const dealerProfileFormSchema = profileFormSchema.extend({
  email: emailBase
    .transform((v) => (v === '' ? null : v))
    .pipe(emailAddressSchema(EMAIL_MESSAGE).nullable()),
});
export type ProfileFormInput = z.input<typeof dealerProfileFormSchema>;
export type ProfileFormValues = z.output<typeof dealerProfileFormSchema>;
export type ProfileUpdateBody = Pick<ProfileFormValues, 'firstName' | 'lastName' | 'email'>;

export function toFormValues(profile: Profile): ProfileFormInput {
  return {
    firstName: profile.firstName,
    lastName: profile.lastName,
    email: profile.email ?? '',
    phone: formatPhoneUS(profile.phone),
    role: profile.roleLabel,
  };
}

/** §10a/§10b: parsed (trimmed, email lower-cased) values equal what was loaded → no request. */
export function isUnchangedProfile(values: ProfileFormValues, loaded: Profile): boolean {
  return (
    values.firstName === loaded.firstName.trim() &&
    values.lastName === loaded.lastName.trim() &&
    (values.email ?? '') === (loaded.email ?? '').trim().toLowerCase()
  );
}

/** Every profile send/resend response (context.md §OTP mechanics). `maskedPhone` is the last four
 *  digits (greenway_api admin-otp-flow.base.ts). Profile-local: auth's `OtpDispatch` has no mask. */
export const profileOtpDispatchSchema = z.object({
  referenceId: z.string(),
  maskedPhone: z.string(),
  otpExpiresAt: z.string(),
  resendCooldownSeconds: z.number(),
  resendAttemptsRemaining: z.number(),
});
export type ProfileOtpDispatch = z.infer<typeof profileOtpDispatchSchema>;

/** `PUT change-password` → `{ sessionsRevoked, accessToken? }`. Grilling Q4: a Dealer's change revokes
 *  nothing and returns no token, so the token is optional for every Role; only it is read. */
export const changePasswordResultSchema = z.object({ accessToken: z.string().min(1).optional() });
export type ChangePasswordResult = z.infer<typeof changePasswordResultSchema>;
